import React, { useState } from 'react';
import { Form, Input, Button, Table, message } from 'antd';
import { useCreateInvitation, useValidateOTP, useGetInvitations, useInvitationExists } from '@/hooks/invitations/invitations';
import { InvitationDTO, InvitationFilterDTO, Pageable } from '@/shared/api/model';

const InvitationManagement: React.FC = () => {
    const [createForm] = Form.useForm();
    const [validateForm] = Form.useForm();
    const createInvitation = useCreateInvitation();
    const validateOTP = useValidateOTP();
    const invitationExists = useInvitationExists();
    const [filter, setFilter] = useState<InvitationFilterDTO>({});
    const [pageable, setPageable] = useState<Pageable>({
        page: 0,
        size: 10,
        sort: ['createdAt,DESC']
    });
    const { data, isLoading, error } = useGetInvitations(filter, pageable);

    const handleCreateInvitation = async (values: InvitationDTO) => {
        try {
            const exists = await invitationExists.mutateAsync(values);
            if(exists){
                message.error('Invitation already exists for this phone number.');
                return;
            }
            await createInvitation.mutateAsync(values);
            message.success('Invitation created successfully!');
            createForm.resetFields();
        } catch (error) {
            message.error('Failed to create invitation.');
        }
    };

    const handleValidateOTP = async (values: InvitationDTO) => {
        try {
            await validateOTP.mutateAsync(values);
            message.success('OTP validated successfully!');
            validateForm.resetFields();
        } catch (error) {
            message.error('Failed to validate OTP.');
        }
    };

    const handleSearch = (values: InvitationFilterDTO) => {
        setFilter(values);
    };

    const handleTableChange = (pagination: any, filters: any, sorter: any): void => {
        // Convert sorter to Spring format and ensure it's an array of strings
        let sortArray: string[] = ['createdAt,DESC']; // default sort
        
        if (sorter.field) {
            const direction = sorter.order === 'ascend' ? 'ASC' : 'DESC';
            sortArray = [`${sorter.field},${direction}`];
        }
        
        setPageable({
            page: pagination.current - 1,
            size: pagination.pageSize,
            sort: sortArray
        });
    };

    return (
        <div>
            <Form
                form={createForm}
                onFinish={handleCreateInvitation}
                layout="vertical"
            >
                <Form.Item 
                    name="phoneNumber" 
                    label="Phone Number" 
                    rules={[{ required: true, message: 'Please input the phone number!' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item 
                    name="name" 
                    label="Name" 
                    rules={[{ required: true, message: 'Please input the name!' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item 
                    name="email" 
                    label="Email"
                >
                    <Input type="email" />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={createInvitation.isPending}>
                    Create Invitation
                </Button>
            </Form>

            <h2>Invitations List</h2>
            <Table 
                dataSource={data?.content} 
                loading={isLoading}
                onChange={handleTableChange}
                pagination={{
                    current: data?.number ? data.number + 1 : 1,
                    pageSize: data?.size || 10,
                    total: data?.totalElements || 0,
                }}
                columns={[
                    {
                        title: 'Phone Number',
                        dataIndex: 'phoneNumber',
                        key: 'phoneNumber',
                    },
                    {
                        title: 'Name',
                        dataIndex: 'name',
                        key: 'name',
                    },
                    {
                        title: 'Email',
                        dataIndex: 'email',
                        key: 'email',
                    },
                    {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                    },
                ]}
            />
        </div>
    );
};

export default InvitationManagement;